import { Stack } from '@mui/material';
import React from 'react';
import { useIntl, defineMessages } from 'react-intl';

import RoundedButton from '@components/RoundedButton';
import TextInputBox from '@components/TextInputBox';

import { useValidateCouponLazyQuery } from 'generated/stripe.generated';
import type { StripeCoupon } from 'generated/types';

const messages = defineMessages({
  couponCode: {
    id: 'couponInput.couponCode',
    defaultMessage: 'Coupon Code',
  },
  invalidCoupon: {
    id: 'couponInput.invalidCoupon',
    defaultMessage: 'Invalid Coupon Code',
  },
  couponVerify: {
    id: 'couponInput.couponVerify',
    defaultMessage: 'Verify',
  },
  couponError: {
    id: 'couponInput.couponError',
    defaultMessage: 'Invalid coupon code',
  },
  couponCheckout: {
    id: 'couponInput.couponCheckout',
    defaultMessage: 'Applied on next page',
  },
});

interface CouponInputProps {
  /** Callback that fires when the verify button is clicked */
  onVerify?: (coupon: StripeCoupon | null) => void;
  /** Show checkout message */
  showCheckoutMsg?: boolean;
}

function CouponInput({ onVerify, showCheckoutMsg = false }: CouponInputProps) {
  const { formatMessage } = useIntl();

  const [coupon, setCoupon] = React.useState<string>();
  const [error, setError] = React.useState<string>();

  const [validateCoupon, { data: couponData, loading: validating }] =
    useValidateCouponLazyQuery({
      fetchPolicy: 'no-cache',
    });

  const handleVerify = async () => {
    if (!coupon) {
      setError('');
      return null;
    }
    const response = await validateCoupon({
      variables: { input: { code: coupon } },
    });
    const couponData = response.data?.validateCoupon ?? null;
    const valid = couponData?.valid;
    setError(valid ? '' : formatMessage(messages.couponError));
    onVerify?.(couponData);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCoupon(value);
  };

  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <div style={{ width: '75%' }}>
        <TextInputBox
          label={formatMessage(messages.couponCode)}
          helperText={error ? error : couponData?.validateCoupon.name}
          sublabel={
            showCheckoutMsg ? formatMessage(messages.couponCheckout) : undefined
          }
          onChange={handleChange}
          value={coupon}
          color={error ? 'error' : 'success'}
        />
      </div>
      <RoundedButton autoSize disabled={validating} onClick={handleVerify}>
        {formatMessage(messages.couponVerify)}
      </RoundedButton>
    </Stack>
  );
}

export default CouponInput;
